/**
 * DataConfiguration Module
 *
 * Configuration data for office employee payslip fields.
 * Defines the structure and mapping of all payslip sections including:
 * - Payment items (支給)
 * - Deduction items (控除)
 * - Attendance items (勤怠)
 * - Bonus payment items (賞与支給)
 * - Bonus deduction items (賞与控除)
 * - Bonus attendance items (賞与勤怠)
 * - Salary notes items (給与その他)
 * - Bonus notes items (賞与其の他)
 * - Memo items (個人メッセージ)
 *
 * @module DataConfiguration
 */

/**
 * Payment section items configuration.
 * Contains items related to salary payments and allowances.
 *
 * @constant
 */
export const paymentItems = [
  { id: 'basic_salary', name: '基本給', keyId: 'p_k1', valueId: 'p_v1' },
  { id: 'position_allowance', name: '役職手当', keyId: 'p_k2', valueId: 'p_v2' },
  { id: 'family_allowance', name: '家族手当', keyId: 'p_k3', valueId: 'p_v3' },
  { id: 'housing_allowance', name: '住宅手当', keyId: 'p_k4', valueId: 'p_v4' },
  { id: 'qualification_allowance', name: '資格手当', keyId: 'p_k5', valueId: 'p_v5' },
  { id: 'diligence_allowance', name: '精勤手当', keyId: 'p_k6', valueId: 'p_v6' },
  { id: 'site_allowance', name: '現場手当', keyId: 'p_k7', valueId: 'p_v7' },
  { id: 'overtime_allowance', name: '時間外手当', keyId: 'p_k8', valueId: 'p_v8' },
  { id: 'other_allowances', name: 'その他手当', keyId: 'p_k9', valueId: 'p_v9' },
  { id: 'year_end_adjustment', name: '年末調整額', keyId: 'p_k10', valueId: 'p_v10' },
  { id: 'long_service_award', name: '永年勤続表彰', keyId: 'p_k11', valueId: 'p_v11' },
  { id: 'total_allowance', name: '他手当計', keyId: 'p_k12', valueId: 'p_v12' },
  { id: 'regular_overtime', name: '普通残業', keyId: 'p_k13', valueId: 'p_v13' },
  { id: 'night_overtime', name: '深夜残業', keyId: 'p_k14', valueId: 'p_v14' },
  { id: 'holiday_overtime', name: '休出残業', keyId: 'p_k15', valueId: 'p_v15' },
  { id: 'holiday_night', name: '休出深夜', keyId: 'p_k16', valueId: 'p_v16' },
  { id: 'legal_holiday_overtime', name: '法休残業', keyId: 'p_k17', valueId: 'p_v17' },
  { id: 'legal_holiday_night', name: '法休深夜', keyId: 'p_k18', valueId: 'p_v18' },
  { id: 'legal_overtime', name: '法内残業', keyId: 'p_k19', valueId: 'p_v19' },
  { id: 'absence_deduction', name: '欠勤控除', keyId: 'p_k20', valueId: 'p_v20' },
  { id: 'late_early_deduction', name: '遅早控除', keyId: 'p_k21', valueId: 'p_v21' },
  { id: 'taxable_commute', name: '課税通勤費', keyId: 'p_k22', valueId: 'p_v22' },
  { id: 'non_taxable_commute', name: '非課税通勤費', keyId: 'p_k23', valueId: 'p_v23' },
  { id: 'taxable_meal', name: '課税食事代', keyId: 'p_k24', valueId: 'p_v24' },
  { id: 'non_taxable_meal', name: '非課税食事代', keyId: 'p_k25', valueId: 'p_v25' },
  { id: 'taxable_night_duty', name: '課税宿直', keyId: 'p_k26', valueId: 'p_v26' },
  { id: 'non_taxable_night_duty', name: '非課税宿直', keyId: 'p_k27', valueId: 'p_v27' },
  { id: 'taxable_raise', name: '課税昇給額', keyId: 'p_k28', valueId: 'p_v28' },
  { id: 'non_taxable_raise', name: '非課税昇給額', keyId: 'p_k29', valueId: 'p_v29' },
  { id: 'taxable_payment', name: '課税支給額', keyId: 'p_k30', valueId: 'p_v30' },
  { id: 'non_taxable_payment', name: '非課支給額', keyId: 'p_k31', valueId: 'p_v31' },
  { id: 'taxable_payment_cumulative', name: '課税支給累計', keyId: 'p_k32', valueId: 'p_v32' },
  { id: 'total_payment', name: '支給合計', keyId: 'p_k33', valueId: 'p_v33' }
];

/**
 * Deduction section items configuration.
 * Contains items related to salary deductions (insurance, taxes, etc.).
 *
 * @constant
 */
export const deductionItems = [
  { id: 'health_insurance', name: '健康保険', keyId: 'd_k1', valueId: 'd_v1' },
  { id: 'nursing_insurance', name: '介護保険', keyId: 'd_k2', valueId: 'd_v2' },
  { id: 'pension_insurance', name: '厚生年金', keyId: 'd_k3', valueId: 'd_v3' },
  { id: 'pension_fund', name: '厚生基金', keyId: 'd_k4', valueId: 'd_v4' },
  { id: 'employment_insurance', name: '雇用保険', keyId: 'd_k5', valueId: 'd_v5' },
  { id: 'social_insurance_total', name: '社保合計', keyId: 'd_k6', valueId: 'd_v6' },
  { id: 'taxable_amount', name: '課税対象額', keyId: 'd_k7', valueId: 'd_v7' },
  { id: 'income_tax', name: '所得税', keyId: 'd_k8', valueId: 'd_v8' },
  { id: 'residence_tax', name: '住民税', keyId: 'd_k9', valueId: 'd_v9' },
  { id: 'residence_tax_correction', name: '住民税訂正', keyId: 'd_k10', valueId: 'd_v10' },
  { id: 'savings', name: '財形貯蓄', keyId: 'd_k11', valueId: 'd_v11' },
  { id: 'rent', name: '家賃', keyId: 'd_k12', valueId: 'd_v12' }
];

/**
 * Attendance section items configuration.
 * Contains items related to work attendance and time tracking.
 *
 * @constant
 */
export const attendanceItems = [
  { id: 'work_days', name: '就業日数', keyId: 'a_k1', valueId: 'a_v1' },
  { id: 'attendance_days', name: '出勤日数', keyId: 'a_k2', valueId: 'a_v2' },
  { id: 'absence_days', name: '欠勤日数', keyId: 'a_k3', valueId: 'a_v3' },
  { id: 'paid_leave_days', name: '有休日数', keyId: 'a_k4', valueId: 'a_v4' },
  { id: 'special_leave_days', name: '特休日数', keyId: 'a_k5', valueId: 'a_v5' },
  { id: 'holiday_work_days', name: '休出日数', keyId: 'a_k6', valueId: 'a_v6' },
  { id: 'legal_holiday_work_days', name: '法内休出日数', keyId: 'a_k7', valueId: 'a_v7' },
  { id: 'compensatory_leave_days', name: '代休日数', keyId: 'a_k8', valueId: 'a_v8' },
  { id: 'meal_count', name: '食事回数', keyId: 'a_k9', valueId: 'a_v9' },
  { id: 'night_duty_count', name: '宿直回数', keyId: 'a_k10', valueId: 'a_v10' },
  { id: 'late_early_count', name: '遅早回数', keyId: 'a_k11', valueId: 'a_v11' },
  { id: 'work_hours', name: '出勤時間', keyId: 'a_k12', valueId: 'a_v12' },
  { id: 'late_early_hours', name: '遅早時間', keyId: 'a_k13', valueId: 'a_v13' },
  { id: 'regular_overtime_hours', name: '普通残業時間', keyId: 'a_k14', valueId: 'a_v14' },
  { id: 'night_overtime_hours', name: '深夜残業時間', keyId: 'a_k15', valueId: 'a_v15' },
  { id: 'holiday_overtime_hours', name: '休出残業時間', keyId: 'a_k16', valueId: 'a_v16' },
  { id: 'holiday_night_hours', name: '休出深夜時間', keyId: 'a_k17', valueId: 'a_v17' },
  { id: 'legal_holiday_overtime_hours', name: '法休残業時間', keyId: 'a_k18', valueId: 'a_v18' },
  { id: 'legal_holiday_night_hours', name: '法休深夜時間', keyId: 'a_k19', valueId: 'a_v19' },
  { id: 'legal_overtime_hours', name: '法内残業時間', keyId: 'a_k20', valueId: 'a_v20' },
  { id: 'dependents_count', name: '扶養等の数', keyId: 'a_k21', valueId: 'a_v21' },
  { id: 'remaining_paid_leave', name: '有休残日数', keyId: 'a_k22', valueId: 'a_v22' }
];

/**
 * Bonus Payment section items configuration.
 * Contains items related to bonus payments and allowances.
 *
 * @constant
 */
export const bonusPaymentItems = [
  { id: 'bonus_payment_1', name: '賞与', keyId: 'bp_k1', valueId: 'bp_v1' },
  { id: 'bonus_payment_2', name: '業績賞与', keyId: 'bp_k2', valueId: 'bp_v2' },
  { id: 'bonus_payment_3', name: '役員賞与', keyId: 'bp_k3', valueId: 'bp_v3' },
  { id: 'bonus_payment_4', name: '報奨金', keyId: 'bp_k4', valueId: 'bp_v4' },
  { id: 'bonus_payment_5', name: '他手当計', keyId: 'bp_k5', valueId: 'bp_v5' },
  { id: 'bonus_payment_6', name: '課税支給額', keyId: 'bp_k6', valueId: 'bp_v6' },
  { id: 'bonus_payment_7', name: '非課支給額', keyId: 'bp_k7', valueId: 'bp_v7' }
];

/**
 * Bonus Deduction section items configuration.
 * Contains items related to bonus deductions (insurance, taxes, etc.).
 *
 * @constant
 */
export const bonusDeductionItems = [
  { id: 'bonus_deduction_1', name: '健康保険', keyId: 'bd_k1', valueId: 'bd_v1' },
  { id: 'bonus_deduction_2', name: '介護保険', keyId: 'bd_k2', valueId: 'bd_v2' },
  { id: 'bonus_deduction_3', name: '厚生年金', keyId: 'bd_k3', valueId: 'bd_v3' },
  { id: 'bonus_deduction_4', name: '厚生基金', keyId: 'bd_k4', valueId: 'bd_v4' },
  { id: 'bonus_deduction_5', name: '雇用保険', keyId: 'bd_k5', valueId: 'bd_v5' },
  { id: 'bonus_deduction_6', name: '所得税', keyId: 'bd_k6', valueId: 'bd_v6' },
  { id: 'bonus_deduction_7', name: '旅行積立', keyId: 'bd_k7', valueId: 'bd_v7' },
  { id: 'bonus_deduction_8', name: '財形貯蓄', keyId: 'bd_k8', valueId: 'bd_v8' },
  { id: 'bonus_deduction_9', name: '組合費', keyId: 'bd_k9', valueId: 'bd_v9' },
  { id: 'bonus_deduction_10', name: '他控除計', keyId: 'bd_k10', valueId: 'bd_v10' },
  { id: 'bonus_deduction_11', name: '社保合計', keyId: 'bd_k11', valueId: 'bd_v11' },
  { id: 'bonus_deduction_12', name: '法廷控除', keyId: 'bd_k12', valueId: 'bd_v12' },
  { id: 'bonus_deduction_13', name: '法廷外控除', keyId: 'bd_k13', valueId: 'bd_v13' }
];

/**
 * Bonus Attendance section items configuration.
 * Contains items related to bonus attendance information.
 *
 * @constant
 */
export const bonusAttendanceItems = [
  { id: 'bonus_attendance_1', name: '扶養の数', keyId: 'ba_k1', valueId: 'ba_v1' }
];

/**
 * Salary Notes section items configuration.
 * Contains items related to salary notes (actual payment, bank transfers, etc.).
 *
 * @constant
 */
export const salaryNotesItems = [
  { id: 'salary_notes_1', name: '実支給額', keyId: 'sn_k1', valueId: 'sn_v1' },
  { id: 'salary_notes_2', name: '振込金額１', keyId: 'sn_k2', valueId: 'sn_v2' },
  { id: 'salary_notes_3', name: '振込金額２', keyId: 'sn_k3', valueId: 'sn_v3' },
  { id: 'salary_notes_4', name: '振込金額３', keyId: 'sn_k4', valueId: 'sn_v4' },
  { id: 'salary_notes_5', name: '現金支給額', keyId: 'sn_k5', valueId: 'sn_v5' },
  { id: 'salary_notes_6', name: '現物支給額', keyId: 'sn_k6', valueId: 'sn_v6' },
  { id: 'salary_notes_7', name: '定額減税', keyId: 'sn_k7', valueId: 'sn_v7' },
  { id: 'salary_notes_8', name: '課税対象額', keyId: 'sn_k8', valueId: 'sn_v8' },
  { id: 'salary_notes_9', name: '差引支給額', keyId: 'sn_k9', valueId: 'sn_v9' }
];

/**
 * Bonus Notes section items configuration.
 * Contains items related to bonus notes (actual payment, bank transfers, etc.).
 *
 * @constant
 */
export const bonusNotesItems = [
  { id: 'bonus_notes_1', name: '実支給額', keyId: 'bn_k1', valueId: 'bn_v1' },
  { id: 'bonus_notes_2', name: '振込金額１', keyId: 'bn_k2', valueId: 'bn_v2' },
  { id: 'bonus_notes_3', name: '振込金額２', keyId: 'bn_k3', valueId: 'bn_v3' },
  { id: 'bonus_notes_4', name: '振込金額３', keyId: 'bn_k4', valueId: 'bn_v4' },
  { id: 'bonus_notes_5', name: '現金支給額', keyId: 'bn_k5', valueId: 'bn_v5' },
  { id: 'bonus_notes_6', name: '現物支給額', keyId: 'bn_k6', valueId: 'bn_v6' },
  { id: 'bonus_notes_7', name: '課税対象額', keyId: 'bn_k7', valueId: 'bn_v7' },
  { id: 'bonus_notes_8', name: '差引支給額', keyId: 'bn_k8', valueId: 'bn_v8' }
];

/**
 * Memo section items configuration.
 * Contains items related to personal messages displayed on payslip.
 *
 * @constant
 */
export const memoItems = [
  { id: 'personal_message_1', name: '個人メッセージ1', keyId: 'm_k1', valueId: 'm_v1' },
  { id: 'personal_message_2', name: '個人メッセージ2', keyId: 'm_k2', valueId: 'm_v2' }
];
